"use client";

import dynamic from "next/dynamic";
import Badge from "@ishvaram/ui/Badge";
import Typography from "@ishvaram/ui/Typography";
import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";
import { useDeferredToolActivation } from "@/components/tool-shell/useDeferredToolActivation";

// Bundle-size discipline (2026-07-16 trim pass): RemediesClient pulls the full
// BirthDetailsForm + PlaceAutocomplete + remedies result stack. Loading it via
// next/dynamic keeps that stack out of the /remedies, /hi/remedies, and
// /hi/upay route chunks and shares ONE copy with the other deferred calculator
// islands. Pre-activation this wrapper renders the SAME hero + a
// height-reserving shell (rules 43/61 tool-first; rule 58 reserved swap).
const RemediesToolIsland = dynamic(() => import("./RemediesClient"), {
  ssr: false,
  loading: () => (
    <div className="max-w-xl mx-auto">
      <BirthToolFormShell />
    </div>
  ),
});

export default function DeferredRemediesClient({
  defaultLanguage = "en",
}: { defaultLanguage?: "en" | "hi" } = {}) {
  const isHindi = defaultLanguage === "hi";
  const { ready, activate } = useDeferredToolActivation();

  if (ready) return <RemediesToolIsland defaultLanguage={defaultLanguage} />;

  // Mirrors RemediesClient's pre-result surface (hero + form) so activation
  // swaps in place without moving the layout.
  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12 sm:py-16">
        <div className="text-center mb-12">
          <Badge
            label={isHindi ? "व्यक्तिगत वैदिक उपाय" : "Personalized Vedic Remedies"}
            variant="accent"
            className="mb-4"
          />
          <Typography
            variant="headline"
            className="text-[28px] sm:text-[36px] font-bold leading-tight text-ish-dark font-serif"
          >
            {isHindi ? "अपने दोष के उपाय जानें" : "Discover Your Dosha Remedies"}
          </Typography>
          <Typography
            variant="body"
            className="mt-4 text-[15px] leading-relaxed max-w-xl mx-auto text-ish-muted"
          >
            {isHindi
              ? "अपनी जन्म कुंडली में मौजूद दोषों की पहचान करें और ईश्वरम के सिद्ध (अभिमंत्रित) उत्पादों के साथ व्यक्तिगत उपाय बंडल पाएं।"
              : "Enter your birth details to discover which doshas are in your chart and get personalized remedy bundles with Siddh (energized) products from Ishvaram."}
          </Typography>
        </div>

        <div className="max-w-xl mx-auto">
          <BirthToolFormShell
            language={defaultLanguage}
            submitLabel={isHindi ? "मेरे उपाय देखें" : "Check My Remedies"}
            onActivate={activate}
          />
        </div>
      </div>
    </div>
  );
}
